import React, { useEffect, useMemo } from 'react'
import { Formik, Field } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { isEmpty, isNil } from 'lodash'
import { IconFilter } from '@tabler/icons-react'

import { TextField, Select, MultiSelect } from '@/components/forms/formik'
import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { OrganizationFilter } from '@/components/filters/OrganizationFilter'
import { RequiredIndicator } from '@/components/forms/RequiredIndicator'
import { getOrganizationOptions } from '@/utils/helpers'
import { setToolsFilter } from '@/reducers/tools/tools.redux'
import {
  createTool,
  updateTool,
  fetchToolFormOptions,
  fetchToolFieldTypes,
} from '@/reducers/tools/tools.actions'
import { fetchModels } from '@/reducers/prompts/prompts.actions'

import { LookupToolFields } from './LookupToolFields'
import { LookupTypeFields } from './LookupTypeFields'
import { AutoCaptureFields } from './AutoCaptureFields'
import { ResetModelWhenItIgnoresUrls } from './ResetModelWhenItIgnoresUrls'
import { ResetConfigWhenTypeChanges } from './ResetConfigWhenTypeChanges'
import { SeedLookupQuestionWhenEntityChanges } from './SeedLookupQuestionWhenEntityChanges'
import { DEFAULT_TOOL_FIELD_LIMITS, TOOL_TYPES, toolTypeOptions } from './tools.constants'
import {
  buildToolFormValues,
  buildToolPayload,
  buildToolValidationSchema,
  formatServerErrors,
  getToolFieldErrors,
} from './tools.helpers'

import './Tools.scss'

export const ToolForm = ({ tool }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const {
    filters,
    agentOptions,
    playbookOptions,
    formOrganization,
    loading,
    fieldTypes,
    fieldTypeLimits,
  } = useSelector((state) => state.tools)
  const { organizations } = useSelector((state) => state)
  const currentUser = useSelector((state) => state.currentUser)
  const { modelOptions: { qa_copilot: qaCopilotModelOptions = [] } = {} } = useSelector(
    (state) => state.prompts
  )

  const isCreate = isNil(tool)
  // Only Balto (org 1) creates Lookup and Auto Capture tools while they're validated in
  // staging; the API refuses everyone else too.
  const isBaltoAdmin = currentUser.organizationid === 1
  const typeOptions = isBaltoAdmin ? toolTypeOptions : []
  const organizationOptions = getOrganizationOptions(organizations)
  const limits = fieldTypeLimits || DEFAULT_TOOL_FIELD_LIMITS

  // The knowledge base runs on agent-gpt, so it needs AI Answers on the organization the tool
  // is for, which is only known once that organization has loaded.
  const organizationResolved = !isNil(formOrganization)
  const sharePointAvailable = Boolean(formOrganization?.agent_gpt_enabled)

  useEffect(() => {
    dispatch(fetchModels())
    dispatch(fetchToolFieldTypes())
  }, [])

  useEffect(() => {
    if (!isCreate && tool?.organization_id && !filters.organization_id) {
      dispatch(setToolsFilter('organization_id', tool.organization_id))
    }
  }, [tool])

  // Landing on /tools/create directly (refresh, or a pasted URL) leaves the filter empty,
  // because it is only ever set by ToolsPage. Seed it the way ToolsPage does.
  useEffect(() => {
    if (isCreate && !filters.organization_id && currentUser.organizationid) {
      dispatch(setToolsFilter('organization_id', currentUser.organizationid))
    }
  }, [currentUser.organizationid])

  useEffect(() => {
    if (filters.organization_id) {
      dispatch(fetchToolFormOptions(filters.organization_id))
    }
  }, [filters.organization_id])

  const handleOrgChange = (option, action) => {
    if (action.action === 'clear') {
      dispatch(setToolsFilter('organization_id', ''))
    } else {
      dispatch(setToolsFilter('organization_id', option.value))
    }
  }

  const initialValues = useMemo(
    () => buildToolFormValues(tool, { agentOptions, playbookOptions, fieldTypes }),
    [tool, agentOptions, playbookOptions, fieldTypes]
  )
  const validationSchema = useMemo(
    () => buildToolValidationSchema({ fieldTypes, limits }),
    [fieldTypes, limits]
  )

  const handleFormSubmit = async (values, { setFieldError, setFieldTouched }) => {
    const payload = buildToolPayload(values)
    const result = isCreate
      ? await dispatch(createTool(payload, filters.organization_id))
      : await dispatch(updateTool(tool.id, payload))

    if (result.ok) {
      history.push('/tools')
      return
    }

    // The API reports config problems by path; in JSON mode there is no row to point at, so
    // they list under the textarea instead.
    if (values.jsonMode) {
      const message = formatServerErrors(result.error)
      if (!isEmpty(message)) {
        setFieldTouched('configJson', true, false)
        setFieldError('configJson', message)
      }
      return
    }

    Object.entries(getToolFieldErrors(result.error, values.type)).forEach(([path, message]) => {
      setFieldTouched(path, true, false)
      setFieldError(path, message)
    })
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={handleFormSubmit}
    >
      {({ errors, touched, submitCount, handleSubmit, values, setFieldValue, setFieldTouched }) => (
        <div className="tool-form">
          <div className="tool-form-layout">
            <div className="tool-form-main">
              <ResetConfigWhenTypeChanges
                type={values.type}
                saved={initialValues}
                setFieldValue={setFieldValue}
              />
              <SeedLookupQuestionWhenEntityChanges
                entityType={values.entity_type}
                question={values.lookup_question}
                setFieldValue={setFieldValue}
              />
              <ResetModelWhenItIgnoresUrls
                values={values}
                modelOptions={qaCopilotModelOptions}
                setFieldValue={setFieldValue}
              />
              <div className="tool-form-field-row">
                <div className="tool-form__field">
                  <div className="flex label-style">
                    Name
                    <RequiredIndicator />
                  </div>
                  <Field
                    name="name"
                    component={TextField}
                    required
                    autoFocus={isCreate}
                    data-testid="tool-name"
                  />
                </div>
                <div className="tool-form__field">
                  <div className="flex label-style">
                    Type
                    <RequiredIndicator />
                  </div>
                  <Field
                    name="type"
                    component={Select}
                    options={typeOptions}
                    required
                    data-testid="tool-type"
                  />
                </div>
              </div>
              {values.type === TOOL_TYPES.ENTITY_LOOKUP && (
                <div className="tool-form-field-row">
                  <LookupTypeFields
                    fieldTypes={fieldTypes}
                    organizationResolved={organizationResolved}
                    sharePointAvailable={sharePointAvailable}
                  />
                </div>
              )}

              {values.type === TOOL_TYPES.ENTITY_LOOKUP && (
                <LookupToolFields values={values} qaCopilotModelOptions={qaCopilotModelOptions} />
              )}

              {values.type === TOOL_TYPES.FORM && (
                <AutoCaptureFields
                  values={values}
                  errors={errors}
                  touched={touched}
                  submitCount={submitCount}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  fieldTypes={fieldTypes}
                  limits={limits}
                  savedConfig={tool?.config ?? null}
                />
              )}
            </div>
            <aside className="tool-form-aside">
              <Card
                title="Filters"
                icon={<IconFilter size={18} />}
                inlineIcon
                subtitle="By default tools are available to everyone on all playbooks"
              >
                <div className="tool-form__field">
                  <OrganizationFilter
                    options={organizationOptions}
                    value={filters.organization_id}
                    onChange={handleOrgChange}
                    fixedWidth={false}
                  />
                </div>
                <div className="tool-form__field">
                  <div className="label-style">Agents allowed</div>
                  <Field
                    name="user_whitelist"
                    component={MultiSelect}
                    showFullOptions
                    options={agentOptions}
                    condenseOptions={false}
                    data-testid="tool-user-whitelist"
                  />
                </div>
                <div className="tool-form__field">
                  <div className="label-style">Agents blocked</div>
                  <Field
                    name="user_blacklist"
                    component={MultiSelect}
                    showFullOptions
                    options={agentOptions}
                    condenseOptions={false}
                    data-testid="tool-user-blacklist"
                  />
                </div>
                <div className="tool-form__field">
                  <div className="label-style">Playbooks allowed</div>
                  <Field
                    name="playbook_whitelist"
                    component={MultiSelect}
                    showFullOptions
                    options={playbookOptions}
                    condenseOptions={false}
                    data-testid="tool-playbook-whitelist"
                  />
                </div>
                <div className="tool-form__field">
                  <div className="label-style">Playbooks blocked</div>
                  <Field
                    name="playbook_blacklist"
                    component={MultiSelect}
                    showFullOptions
                    options={playbookOptions}
                    condenseOptions={false}
                    data-testid="tool-playbook-blacklist"
                  />
                </div>
              </Card>
            </aside>
          </div>
          <footer className="form-actions sticky">
            <Button onClick={() => history.push('/tools')} data-testid="tool-form-cancel">
              Cancel
            </Button>
            <Button
              brand
              onClick={handleSubmit}
              loading={loading.createTool || loading.updateTool}
              data-testid="tool-form-submit"
            >
              {isCreate ? 'Create tool' : 'Update tool'}
            </Button>
          </footer>
        </div>
      )}
    </Formik>
  )
}
